/**
 * Who works here, and everything about them the office decides.
 *
 * A row per person; their paperwork and their own settings open in dialogs from the row.
 * The three things that are not obvious from the shape of it:
 *
 * A departed driver is still listed, greyed. The screen they are missing from is the only
 * screen they can be brought back on — the same reasoning as a switched-off firm still
 * appearing on the platform list — and their completed rides are still theirs.
 *
 * Hiring asks for an address rather than picking from a list of accounts. The office knows
 * an email; it does not know a user id, and the old procedure — "tell them to register, wait,
 * then grant" — is what made this step need somebody with database access.
 *
 * Or, for a firm that hands its drivers phones, asks for no address at all: the driver signs in
 * on a service device the office gives him, and the dialog that shows its QR code opens as soon
 * as he is on the books.
 *
 * Whoever has an appearance of their own is said on their row rather than given a column.
 * The roster carries `appTheme` for exactly this: an override is rare by design, so a column
 * would be empty for everybody but the one driver who asked.
 */

import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Pill,
  Section,
  Segmented,
  Sub,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  type Tone,
} from '../../ui';
import { ServiceDeviceDialog } from '../../devices/ServiceDeviceDialog';
import { AllowedCarsDialog } from './AllowedCarsDialog';
import { DocumentEditor } from './DocumentEditor';
import { DriverSettingsForm } from './DriverSettingsForm';
import type {
  FleetDriver,
  FleetVehicle,
  HireRequest,
  NewDocument,
  NewDriver,
  ShiftState,
} from './api';

interface Props {
  drivers: FleetDriver[] | null;
  /** The cars, to choose the ones a driver may drive from. */
  vehicles: FleetVehicle[] | null;
  /** Whether drivers take any free car; when false, each row says which cars he may (#370). */
  anyCar: boolean;
  busy: boolean;

  /** The page's last failure, repeated inside a dialog because the dialog covers the page. */
  error: string | null;
  onHire: (request: HireRequest) => Promise<boolean>;
  /** Hires somebody who signs in on a service device: no address. Answers the new driver's id. */
  onHireForDevice: (driver: NewDriver) => Promise<string | null>;
  onUpdate: (driverId: string, driver: NewDriver) => Promise<boolean>;
  onSetActive: (driver: FleetDriver, active: boolean) => void;
  onRecordDocument: (document: NewDocument) => Promise<boolean>;
  onRemoveDocument: (documentId: string) => void;
  onSetAllowed: (driverId: string, vehicleIds: string[]) => Promise<boolean>;
}

const emptyDriver = {
  email: '',
  displayName: '',
  phoneNumber: '',
  callSign: '',
  notes: '',
};

/** Where a driver is, in the one colour vocabulary the console has. */
const SHIFT: Record<ShiftState, Tone> = {
  Available: 'ok',
  Busy: 'info',
  OnBreak: 'warn',
  OffDuty: 'neutral',
};

export function DriverList({
  drivers,
  vehicles,
  anyCar,
  busy,
  error,
  onHire,
  onHireForDevice,
  onUpdate,
  onSetActive,
  onRecordDocument,
  onRemoveDocument,
  onSetAllowed,
}: Props) {
  const { t } = useTranslation();
  const form = useId();

  const [fields, setFields] = useState(emptyDriver);
  const [editing, setEditing] = useState<string | null>(null);
  const [hiring, setHiring] = useState(false);
  const [papersFor, setPapersFor] = useState<string | null>(null);
  const [settingsFor, setSettingsFor] = useState<string | null>(null);
  const [carsFor, setCarsFor] = useState<string | null>(null);
  const [signIn, setSignIn] = useState<'email' | 'device'>('email');
  const [deviceFor, setDeviceFor] = useState<{ id: string; name: string; qr: boolean } | null>(
    null,
  );

  // Both dialogs are held open by an id rather than by the row they were opened from. Every
  // mutation refetches, so a driver kept by value would show the documents he had before the
  // one just recorded.
  const papers = drivers?.find((driver) => driver.id === papersFor) ?? null;
  const settings = drivers?.find((driver) => driver.id === settingsFor) ?? null;
  const cars = drivers?.find((driver) => driver.id === carsFor) ?? null;

  /** The plates a driver may drive, in the order the cars are listed. */
  const allowedPlates = (driver: FleetDriver) =>
    (vehicles ?? [])
      .filter((vehicle) => (driver.allowedVehicleIds ?? []).includes(vehicle.id))
      .map((vehicle) => vehicle.plate);

  function startEdit(driver: FleetDriver) {
    setEditing(driver.id);
    setHiring(false);
    setFields({
      email: '',
      displayName: driver.displayName,
      phoneNumber: driver.phoneNumber ?? '',
      callSign: driver.callSign ?? '',
      notes: driver.notes ?? '',
    });
  }

  function close() {
    setFields(emptyDriver);
    setEditing(null);
    setHiring(false);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    const details: NewDriver = {
      displayName: fields.displayName.trim(),
      phoneNumber: fields.phoneNumber.trim() === '' ? null : fields.phoneNumber.trim(),
      callSign: fields.callSign.trim() === '' ? null : fields.callSign.trim(),
      notes: fields.notes.trim() === '' ? null : fields.notes.trim(),
    };

    if (editing === null && signIn === 'device') {
      const created = await onHireForDevice(details);

      if (created !== null) {
        close();
        setDeviceFor({ id: created, name: details.displayName, qr: true });
      }

      return;
    }

    const done =
      editing !== null
        ? await onUpdate(editing, details)
        : await onHire({ ...details, email: fields.email.trim() });

    // Only now. The form used to empty itself the moment it was submitted, so a rejected
    // hire — a duplicate address, a name the server would not take — threw away everything
    // that had just been typed.
    if (done) close();
  }

  return (
    <Section
      title={t('fleet.drivers.title')}
      description={t('fleet.drivers.help')}
      actions={
        <Button
          size="sm"
          disabled={busy}
          onClick={() => {
            setFields(emptyDriver);
            setEditing(null);
            setHiring(true);
          }}
        >
          <Icon name="plus" small />
          {t('fleet.drivers.hire')}
        </Button>
      }
    >
      {drivers === null ? (
        <EmptyState>{t('app.loading')}</EmptyState>
      ) : drivers.length === 0 ? (
        <EmptyState>{t('fleet.drivers.none')}</EmptyState>
      ) : (
        <Table>
          <thead>
            <Tr>
              <Th>{t('fleet.columns.name')}</Th>
              <Th>{t('fleet.columns.phone')}</Th>
              <Th>{t('fleet.columns.car')}</Th>
              <Th>{t('fleet.columns.state')}</Th>
              <Th />
            </Tr>
          </thead>
          <tbody>
            {drivers.map((driver) => (
              <Tr key={driver.id} className={driver.isActive ? undefined : 'text-ink-3'}>
                <Td wrap>
                  {driver.displayName}
                  {driver.callSign !== null && <Sub block>{driver.callSign}</Sub>}
                  {driver.appTheme !== null && (
                    <Sub block>{t('fleet.drivers.hasAppThemeOverride')}</Sub>
                  )}
                </Td>
                <Td>{driver.phoneNumber ?? t('fleet.drivers.noNumber')}</Td>
                <Td wrap>
                  {driver.vehiclePlate ?? t('fleet.drivers.noVehicle')}
                  {/* Which cars the office allows him, while that is the rule — in the car's own
                      cell, a small button that opens the choice, so the row keeps its four
                      actions. None is said plainly: he has nothing to sign into until somebody
                      chooses. The plates themselves are the button's title. */}
                  {!anyCar && (
                    <span className="block mt-1">
                      <Button
                        size="sm"
                        disabled={busy}
                        title={allowedPlates(driver).join(', ') || undefined}
                        onClick={() => setCarsFor(driver.id)}
                      >
                        {allowedPlates(driver).length === 0
                          ? t('fleet.allowed.choose')
                          : t('fleet.allowed.count', { count: allowedPlates(driver).length })}
                      </Button>
                    </span>
                  )}
                </Td>
                <Td>
                  <span className="flex items-center gap-1.5">
                    <Pill tone={SHIFT[driver.shiftState]}>
                      {t(`shiftStates.${driver.shiftState}`)}
                    </Pill>

                    {driver.documents.some((d) => d.isExpired) && (
                      <Pill tone="danger" soft>
                        {t('fleet.documents.hasExpired')}
                      </Pill>
                    )}
                  </span>
                </Td>
                <Td>
                  <span className="flex justify-end gap-1.5">
                    <Button size="sm" onClick={() => setPapersFor(driver.id)}>
                      {t('fleet.documents.title')}
                    </Button>

                    <Button size="sm" onClick={() => setSettingsFor(driver.id)}>
                      {t('fleet.drivers.settings')}
                    </Button>

                    <Button
                      size="sm"
                      disabled={!driver.isActive}
                      onClick={() => setDeviceFor({ id: driver.id, name: driver.displayName, qr: false })}
                    >
                      {t('devices.row')}
                    </Button>

                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => startEdit(driver)}
                    >
                      {t('fleet.edit')}
                    </Button>

                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => onSetActive(driver, !driver.isActive)}
                    >
                      {driver.isActive
                        ? t('fleet.drivers.offboard')
                        : t('fleet.drivers.reinstate')}
                    </Button>
                  </span>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <Dialog
        open={hiring || editing !== null}
        title={editing !== null ? t('fleet.drivers.editing') : t('fleet.drivers.hiring')}
        onClose={close}
        actions={
          <>
            <Button variant="quiet" onClick={close}>
              {t('fleet.cancel')}
            </Button>
            <Button variant="primary" type="submit" form={form} disabled={busy}>
              {editing !== null ? t('fleet.save') : t('fleet.drivers.hire')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {error !== null && <Banner tone="danger">{error}</Banner>}

          <form id={form} onSubmit={(event) => void submit(event)} className="flex flex-col gap-3">
            {editing === null && (
              <Segmented
                label={t('devices.hire.how')}
                value={signIn}
                options={[
                  { value: 'email', label: t('devices.hire.byEmail') },
                  { value: 'device', label: t('devices.hire.byDevice') },
                ]}
                onChange={setSignIn}
              />
            )}

            {editing === null && signIn === 'device' && (
              <p className="m-0 text-secondary text-ink-3">{t('devices.hire.byDeviceHelp')}</p>
            )}

            {editing === null && signIn === 'email' && (
              <Field id="email" label={t('fleet.drivers.email')} help={t('fleet.drivers.emailHelp')}>
                <Input
                  type="email"
                  value={fields.email}
                  onChange={(event) => setFields({ ...fields, email: event.target.value })}
                  required
                />
              </Field>
            )}

            <Field id="displayName" label={t('fleet.drivers.name')} help={t('fleet.drivers.nameHelp')}>
              <Input
                value={fields.displayName}
                onChange={(event) => setFields({ ...fields, displayName: event.target.value })}
                required
              />
            </Field>

            <Field id="phoneNumber" label={t('fleet.drivers.phone')} help={t('fleet.drivers.phoneHelp')}>
              <Input
                value={fields.phoneNumber}
                onChange={(event) => setFields({ ...fields, phoneNumber: event.target.value })}
              />
            </Field>

            <Field id="callSign" label={t('fleet.drivers.callSign')} help={t('fleet.drivers.callSignHelp')}>
              <Input
                value={fields.callSign}
                onChange={(event) => setFields({ ...fields, callSign: event.target.value })}
              />
            </Field>

            <Field id="notes" label={t('fleet.drivers.notes')} help={t('fleet.drivers.notesHelp')}>
              <Textarea
                value={fields.notes}
                onChange={(event) => setFields({ ...fields, notes: event.target.value })}
              />
            </Field>
          </form>
        </div>
      </Dialog>

      {papers !== null && (
        <DocumentEditor
          ownerType="Driver"
          ownerId={papers.id}
          ownerName={papers.displayName}
          documents={papers.documents}
          busy={busy}
          error={error}
          onRecord={onRecordDocument}
          onRemove={onRemoveDocument}
          onClose={() => setPapersFor(null)}
        />
      )}

      {cars !== null && (
        <AllowedCarsDialog
          driver={cars}
          vehicles={vehicles ?? []}
          busy={busy}
          error={error}
          onSave={(vehicleIds) => onSetAllowed(cars.id, vehicleIds)}
          onClose={() => setCarsFor(null)}
        />
      )}

      {deviceFor !== null && (
        <ServiceDeviceDialog
          driverId={deviceFor.id}
          driverName={deviceFor.name}
          startWithQr={deviceFor.qr}
          onClose={() => setDeviceFor(null)}
        />
      )}

      {settings !== null && (
        <DriverSettingsForm
          driverId={settings.id}
          driverName={settings.displayName}
          onClose={() => setSettingsFor(null)}
        />
      )}
    </Section>
  );
}
